/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { MessageService } from '@src/app/shared/message.service';
import { TenantService } from '@src/app/shared/tenant.service';
import { AbstractObject } from '@src/app/model/abstract-object';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { FormUrlComponent } from '@src/app/shared/form/form-url/form-url.component';
import { TableDialogComponent } from '@src/app/shared/dialogs/table-dialog/table-dialog.component';

class PasswordReset extends AbstractObject {
    email: string;
    createdAt: number;        // unix timestamp (seconds)
    activatedAt: number | null;
    expired: boolean;         // set server-side: token window elapsed and never activated
    resetUrl: string;
}

@Component({
    selector: 'app-password-resets',
    imports: [MatButtonModule],
    template: `
        @if (allowed) {
            <button mat-raised-button color="accent" (click)="showResets()">
                Password Resets
            </button>
        }
    `
})
export class PasswordResetsComponent {
    private http = inject(HttpClient);
    private dialog = inject(MatDialog);
    private messageService = inject(MessageService);
    private currentUserSvc = inject(CurrentUserService);

    protected get allowed(): boolean {
        return this.currentUserSvc.hasUber();
    }

    showResets() {
        this.http.get<PasswordReset[]>(TenantService.bareAjaxPath + 'uber/password-resets').subscribe({
            next: (resets) => this.openDialog(resets),
            error: (err) => this.messageService.showErrorMessage(err.error?.message ?? 'Could not load password resets'),
        });
    }

    private openDialog(resets: PasswordReset[]) {
        const fields = [
            FormTextComponent.make('Email', 'email'),
            FormDateTimeComponent.make('Requested', 'createdAt'),
            FormTextComponent.make('Activated', 'activatedAt', {
                calculateValue: (r: PasswordReset) => {
                    if (r.activatedAt) {
                        return new Date(r.activatedAt * 1000).toLocaleString();
                    }
                    return r.expired ? 'Expired' : 'Not yet';
                },
            }),
            FormUrlComponent.make('Reset URL', 'resetUrl'),
        ];

        const config = new FormConfig({
            title: 'Password Resets (last 50)',
            fieldSet: new FieldSet({ fields }),
            mode: 'list',
        });

        this.dialog.open(TableDialogComponent, {
            data: { config, items: resets },
            width: '90vw',
            maxWidth: '1200px',
        });
    }
}
